import {
  IFoodItem,
  IFoodMenuCategory,
} from "../../../../redux/food/food.types";
import { IExceededLimit } from "../types/menuBuilder.types";
import { parseSelectionKey } from "./selectionKey";

/**
 * Returns the placed categoryUuid for an item by reading _selectionKey first,
 * then falling back to category_list[0] for un-stamped items.
 */
const getPlacedCategoryUuid = (item: IFoodItem): string => {
  const key = (item as any)._selectionKey as string | undefined;
  if (key) return parseSelectionKey(key).categoryUuid;
  return item.category_list?.[0]?.category_uuid ?? "";
};

/**
 * Returns the placed subCategoryUuid for an item by reading _selectionKey first,
 * then falling back to sub_category_uuid for un-stamped items.
 */
const getPlacedSubCategoryUuid = (item: IFoodItem): string | null => {
  const key = (item as any)._selectionKey as string | undefined;
  if (key) return parseSelectionKey(key).subCategoryUuid;
  return item.sub_category_uuid ?? null;
};

/**
 * Returns only items placed into the given subcategoryUuid (from _selectionKey).
 */
export const filterItemsBySubcategory = (
  items: IFoodItem[],
  subcategoryUuid: string,
): IFoodItem[] => {
  return items.filter((item) => getPlacedSubCategoryUuid(item) === subcategoryUuid);
};

/**
 * Counts items placed into the given categoryUuid (from _selectionKey).
 */
export const getCategoryItemCount = (
  selectedItems: IFoodItem[],
  categoryUuid: string,
): number => {
  return selectedItems.filter(
    (item) => getPlacedCategoryUuid(item) === categoryUuid,
  ).length;
};

/**
 * Counts items placed into the given subcategoryUuid (from _selectionKey).
 */
export const getSubcategoryItemCount = (
  selectedItems: IFoodItem[],
  subcategoryUuid: string,
): number => {
  return selectedItems.filter(
    (item) => getPlacedSubCategoryUuid(item) === subcategoryUuid,
  ).length;
};

/**
 * Returns true if limit is defined, limit > 0, and currentCount >= limit.
 */
export const isAtLimit = (
  currentCount: number,
  limit: number | undefined,
): boolean => {
  return limit !== undefined && limit > 0 && currentCount >= limit;
};

/**
 * Checks all categories and subcategories against their limits.
 * Returns array of exceeded limits where current > max.
 */
export const getExceededLimits = (
  selectedItems: IFoodItem[],
  categoryLimits: Record<string, number>,
  allCategories: IFoodMenuCategory[],
): IExceededLimit[] => {
  const exceededLimits: IExceededLimit[] = [];

  for (const category of allCategories) {
    const categoryUuid = category.category_uuid;
    if (!categoryUuid) continue;

    const categoryLimit = categoryLimits[categoryUuid];
    if (categoryLimit !== undefined && categoryLimit > 0) {
      const currentCount = getCategoryItemCount(selectedItems, categoryUuid);
      if (currentCount > categoryLimit) {
        exceededLimits.push({
          name: category.category_name,
          current: currentCount,
          max: categoryLimit,
          type: "Category",
        });
      }
    }

    if (category.subcategories) {
      for (const sub of category.subcategories) {
        const subUuid = sub.food_subcategory_name_uuid;
        if (!subUuid) continue;

        const subLimit = categoryLimits[subUuid];
        if (subLimit !== undefined && subLimit > 0) {
          const subCount = getSubcategoryItemCount(selectedItems, subUuid);
          if (subCount > subLimit) {
            exceededLimits.push({
              name: sub.subcategory_name,
              current: subCount,
              max: subLimit,
              type: "Subcategory",
            });
          }
        }
      }
    }
  }

  return exceededLimits;
};
